// Mode 2 browse surface — /app/browse. Shows currently-active programs
// plus "available to add" cards with the returning-member $19 fee.
// Spec: Sprint 5 of VOYA9_Frontend_Assessment_Multiprogram_Modes.md
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import CareTeamFloatingButton from "@/components/CareTeamFloatingButton";
import { apiGet } from "@/lib/api";
import { C } from "@/lib/brand";
import { fmtMoney } from "@/lib/format";
import ProgramColorTile, { ProgramTilesGrid } from "@/components/ProgramColorTile";
import WhatsNextTile from "@/components/WhatsNextTile";

// In-development = admin has flipped "Active" OFF but "On Landing" (aka
// isVisibleOnBrowse) ON. Fully BE-driven — no hardcoded slug list.
// Also accepts forward-compat fields if the BE ever ships an explicit
// `developmentStatus` / `isInDevelopment` signal.
function isInDevelopment(p) {
  if (!p) return false;
  if (p.developmentStatus === "in_development" || p.developmentStatus === "in-development") return true;
  if (p.status === "in_development" || p.status === "in-development") return true;
  if (p.isInDevelopment === true) return true;
  // Canonical signal: admin marked it visible-on-browse but not yet active.
  if (p.isVisibleOnBrowse === true && p.isActive === false) return true;
  return false;
}

export default function Browse() {
  const [programs, setPrograms] = useState(null);
  const [subs, setSubs] = useState(null);
  const [fees, setFees] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiGet("/consumer/programs").catch(() => []),
      apiGet("/consumer/subscriptions").catch(() => []),
      apiGet("/consumer/fees/current").catch(() => ({ additionalProgramConsult: { amount: 1900, displayAmount: "$19" } })),
    ]).then(([p, s, f]) => {
      if (cancelled) return;
      setPrograms(Array.isArray(p) ? p : []);
      setSubs(Array.isArray(s) ? s : []);
      setFees(f || {});
    });
    return () => { cancelled = true; };
  }, []);

  const activeSubs = useMemo(
    () => (subs || []).filter((s) => ["active", "paused", "cancel_at_period_end", "payment_failed"].includes(s.status)),
    [subs],
  );
  const activeProgramIds = useMemo(
    () => new Set(activeSubs.map((s) => s.programId || s.program?._id || s.program?.id).filter(Boolean)),
    [activeSubs],
  );
  const available = useMemo(
    () => (programs || [])
      // Operator controls visibility via "Show in member browse" flag.
      .filter((p) => p?.isVisibleOnBrowse === true)
      // Already-subscribed programs are surfaced in the "Currently active"
      // section above — exclude them from the add list.
      .filter((p) => !activeProgramIds.has(p._id || p.id)),
    [programs, activeProgramIds],
  );

  // Split available into purchasable-now vs. in-development. In-development
  // tiles route to the dedicated /coming-soon detail page; purchasable
  // tiles route to the regular program detail (or info page when !isActive).
  const availableActive = useMemo(
    () => available.filter((p) => !isInDevelopment(p)),
    [available],
  );
  const availableInDev = useMemo(
    () => available.filter((p) => isInDevelopment(p)),
    [available],
  );

  const addFee = fees?.additionalProgramConsult?.displayAmount || "$19";
  const loading = programs === null || subs === null;

  return (
    <div data-testid="browse-page" style={{ background: C.cream, minHeight: "100vh", paddingBottom: 120 }}>
      <section
        style={{
          background: C.ink,
          color: C.cream,
          padding: "64px 24px 96px",
          position: "relative",
          // Section is clipped directly so the bottom edge IS the
          // diagonal — no overlay triangle that could leak a flat
          // sliver between two solid colors.
          clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 32px), 0 100%)",
        }}
      >
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <Eyebrow color={C.peach}>★ BROWSE PROGRAMS</Eyebrow>
          <Headline before="ADD TO YOUR" italic="voya9." color={C.cream} italicColor={C.peach} size={68} className="mt-4" />
          <p style={{ marginTop: 20, fontSize: 15, lineHeight: 1.6, color: "rgba(250,246,240,0.72)", maxWidth: 580 }}>
            Each new program has a separate physician review (<strong style={{ color: C.peach }}>{addFee}</strong>) and runs alongside your active subscriptions.
          </p>
        </div>
      </section>

      <section style={{ padding: "40px 0 12px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.warmGray }}>
            CURRENTLY ACTIVE
          </div>
        </div>
        <div data-testid="browse-active-list" style={{ marginTop: 14, maxWidth: 900, marginLeft: "auto", marginRight: "auto" }}>
          {loading && <div style={{ padding: "0 24px" }}><Spinner size={22} /></div>}
          {!loading && activeSubs.length === 0 && (
            <div style={{ margin: "0 24px", padding: "16px 18px", background: C.bone, color: C.warmGray, fontSize: 14, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
              No active programs yet.
            </div>
          )}
          {activeSubs.length > 0 && (
            <ProgramTilesGrid>
              {activeSubs.map((s) => {
                const matchingProgram = (programs || []).find(
                  (p) => (p._id || p.id) === (s.programId || s.program?._id || s.program?.id) ||
                         (p.slug && s.programSlug && p.slug === s.programSlug),
                );
                const program = matchingProgram || {
                  slug: s.programSlug || (s.programName || "").toLowerCase().replace(/\s+/g, "-"),
                  name: s.programName || s.productName || "Your program",
                  tagline: s.programTagline,
                };
                return (
                  <ProgramColorTile
                    key={s._id || s.id}
                    program={program}
                    variant="active"
                    activeTierName={s.tierName || s.product?.tierName || s.productName || ""}
                    to="/app/plan"
                    testIdPrefix="browse-active"
                  />
                );
              })}
            </ProgramTilesGrid>
          )}
        </div>
      </section>

      <section style={{ padding: "32px 0 24px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.warmGray }}>
            AVAILABLE TO ADD
          </div>
        </div>
        <div data-testid="browse-available-list" style={{ marginTop: 14, maxWidth: 900, marginLeft: "auto", marginRight: "auto" }}>
          {loading && <div style={{ padding: 24 }}><Spinner size={26} /></div>}
          {!loading && availableActive.length === 0 && availableInDev.length === 0 && (
            <div style={{ margin: "0 24px", padding: "20px 18px", background: C.bone, color: C.warmGray, fontSize: 14, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
              You're in every program we offer. More on the way.
            </div>
          )}
          {!loading && availableActive.length === 0 && availableInDev.length > 0 && (
            <div style={{ margin: "0 24px 14px", padding: "16px 18px", background: C.bone, color: C.warmGray, fontSize: 14, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
              No additional programs available to purchase right now — see what's coming next below.
            </div>
          )}
          {availableActive.length > 0 && (
            <ProgramTilesGrid>
              {availableActive.map((p) => (
                <ProgramColorTile
                  key={p._id || p.id || p.slug}
                  program={p}
                  // "ADD" only when active for purchase; otherwise the tile
                  // routes to the in-app program info page (marketing-style
                  // content inside the member shell).
                  variant={p.isActive ? "available_add" : "explore"}
                  testIdPrefix="browse-available"
                />
              ))}
            </ProgramTilesGrid>
          )}
        </div>
      </section>

      {!loading && availableInDev.length > 0 && (
        <section data-testid="browse-in-development-section" style={{ padding: "8px 0 80px" }}>
          <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px" }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.warmGray }}>
              IN DEVELOPMENT
            </div>
            <p style={{ marginTop: 8, fontSize: 13, lineHeight: 1.55, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", maxWidth: 560, margin: "8px 0 0" }}>
              Programs we're building carefully. Join the waitlist for first-cohort pricing and monthly updates.
            </p>
          </div>
          <div data-testid="browse-in-development-list" style={{ marginTop: 14, maxWidth: 900, marginLeft: "auto", marginRight: "auto" }}>
            <ProgramTilesGrid>
              {availableInDev.map((p) => (
                <ProgramColorTile
                  key={p._id || p.id || p.slug}
                  program={p}
                  variant="in_development"
                  testIdPrefix="browse-indev"
                />
              ))}
              {/* Auto-balanced "What's next" tile to finish the grid
                  flush. Spans both desktop columns when the in-dev
                  count is even (so the trailing tile sits full-width);
                  single-cell when odd (fills the empty column slot). */}
              <WhatsNextTile
                forceWide={availableInDev.length % 2 === 0}
                compact
                to="/app/what-we-help-with"
                testId="browse-indev-whats-next"
              />
            </ProgramTilesGrid>
          </div>
        </section>
      )}

      {/* Cross-link to the public "What we help with" concerns catalog —
          a quiet escape hatch for members unsure which program fits. */}
      <section data-testid="browse-not-sure-section" style={{ padding: "8px 0 64px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
          <Link
            to="/app/what-we-help-with"
            data-testid="browse-what-we-help-with-link"
            style={{
              display: "inline-block",
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: "0.18em",
              color: C.ink,
              textDecoration: "none",
              borderBottom: `1.5px solid ${C.ink}`,
              paddingBottom: 4,
              textTransform: "uppercase",
            }}
          >
            Not sure where to start? See what we help with →
          </Link>
        </div>
      </section>

      <CareTeamFloatingButton unreadCount={0} />
    </div>
  );
}
